import {
  faExclamation as ExclamationIcon,
  faEye as EyeIcon,
  type IconDefinition,
  faPen as PenIcon,
  faPlus as PlusIcon,
  faThumbsUp as ThumbsUpIcon
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAtom } from 'jotai';
import { useMemo } from 'react';

import ActivityLog from '@/components/ActivityLog';
import { ticketAtom } from '@/pages/Ticket';
import { TICKET_STATUS_ID } from '@/pages/Tickets/Tickets.constants';
import getDateTime from '@/utils/getDateTime.utils';

const TicketDetailsActivityLog = () => {
  const [{ activities, ticket_type }] = useAtom(ticketAtom);

  const getIcon = (type: string, new_status: string) => {
    const populateTemplate = (icon: IconDefinition) => {
      return <FontAwesomeIcon icon={icon} size="sm" />;
    };

    // we determine what icon to get/display based on the activity type
    switch (type) {
      case 'TicketCreated':
        return populateTemplate(ExclamationIcon);
      case 'TicketUpdated':
      case 'TicketUpdatedInternal':
        return populateTemplate(PenIcon);
      case 'MessageCreated':
      case 'InternalMessageCreated':
        return populateTemplate(PlusIcon);
      case 'MessageUpdated':
      case 'InternalMessageUpdated':
        return populateTemplate(PenIcon);
      case 'StatusUpdated':
        // threat tickets start with a draft state which non-internal users do not see
        // this gives the first activity for non-internal users a different icon
        if (ticket_type === 1) {
          if (new_status === TICKET_STATUS_ID.OPEN)
            return populateTemplate(ExclamationIcon);
        }
        return populateTemplate(ThumbsUpIcon);
      case 'WatchersUpdated':
        return populateTemplate(EyeIcon);
      default:
        return populateTemplate(ExclamationIcon);
    }
  };

  const getActivityMessage = (
    message: string,
    new_status: TICKET_STATUS_ID,
    old_status: TICKET_STATUS_ID
  ) => {
    // threat tickets
    if (message === 'Ticket Created' && new_status === TICKET_STATUS_ID.DRAFT) {
      return 'Ticket Drafted';
    }

    // all tickets
    if (message === 'Status Updated') {
      if (
        [
          TICKET_STATUS_ID.OPEN,
          TICKET_STATUS_ID.ACKNOWLEDGED,
          TICKET_STATUS_ID.REMEDIATED
        ].includes(new_status) &&
        [TICKET_STATUS_ID.COMPLETED, TICKET_STATUS_ID.AUTO_CLOSED].includes(
          old_status
        )
      )
        return 'Ticket Reopened';
      if (new_status === TICKET_STATUS_ID.OPEN) return 'Ticket Created';
      if (new_status === TICKET_STATUS_ID.ACKNOWLEDGED)
        return 'Ticket Acknowledged';
      if (new_status === TICKET_STATUS_ID.REMEDIATED)
        return 'Ticket Remediated';
      if (new_status === TICKET_STATUS_ID.COMPLETED) return 'Ticket Completed';
      if (new_status === TICKET_STATUS_ID.AUTO_CLOSED)
        return 'Ticket Auto Closed';
    }

    return message;
  };

  // take the original activities and insert the icon, message, and massage the created_at date
  const ticketActivites = useMemo(() => {
    return activities?.map((activity) => {
      return {
        ...activity,
        created_at: getDateTime(activity.created_at),
        icon: getIcon(activity.activity_type, activity.new_status as string),
        message: getActivityMessage(
          activity.message,
          activity.new_status as TICKET_STATUS_ID,
          activity.old_status as TICKET_STATUS_ID
        )
      };
    });
  }, [activities]);

  return <ActivityLog activities={ticketActivites || []} />;
};

export default TicketDetailsActivityLog;
